import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { AUTO_COLLAPSE_ROUTES } from '../../config/menuConfig'

/**
 * Hook para manejar el estado del sidebar (colapsado/expandido)
 * Maneja la persistencia en localStorage y auto-colapso según rutas
 */
export const useSidebar = () => {
  const location = useLocation()
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Auto-colapsar si estamos en una ruta específica
    if (AUTO_COLLAPSE_ROUTES.some(route => location.pathname.startsWith(route))) {
      return true
    }
    // Recuperar estado guardado
    try {
      const saved = localStorage.getItem('sidebarCollapsed')
      return saved ? JSON.parse(saved) : false
    } catch {
      return false
    }
  })

  // Auto-colapsar cuando se accede a rutas específicas
  useEffect(() => {
    const shouldCollapse = AUTO_COLLAPSE_ROUTES.some(route => 
      location.pathname.startsWith(route)
    )
    if (shouldCollapse) {
      setIsCollapsed(true)
    }
  }, [location.pathname])

  // Persistir estado en localStorage (excepto en rutas auto-colapsadas)
  useEffect(() => {
    const shouldPersist = !AUTO_COLLAPSE_ROUTES.some(route => 
      location.pathname.startsWith(route)
    )
    if (shouldPersist) {
      try {
        localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed))
      } catch (error) {
        console.error('Error guardando estado del sidebar:', error)
      }
    }
  }, [isCollapsed, location.pathname])

  const toggle = useCallback(() => {
    setIsCollapsed((prev: boolean) => !prev)
  }, [])

  const collapse = useCallback(() => {
    setIsCollapsed(true)
  }, [])

  const expand = useCallback(() => {
    setIsCollapsed(false)
  }, [])

  return {
    isCollapsed,
    toggle,
    collapse,
    expand,
  }
}

